import React, { useEffect, useState } from "react";
import { getCookie } from "../components/Cookie.jsx";
import Switch from "@mui/material/Switch";
import toast from "react-hot-toast";
import AlertDialog from "../components/AlertDialog.jsx";
import DataTable from "react-data-table-component";
import "../../src/index.css";

const customStyles = {
  headCells: {
    style: {
      backgroundColor: "#4880FF",
      color: "white",
      fontWeight: "bold",
      fontSize: "0.9rem",
    },
  },
  cells: {
    style: {
      whiteSpace: "normal",
    },
  },
  table: {
    style: {
      borderRadius: "10px",
      overflow: "hidden",
    },
  },
};

const AdminManageUser = () => {
  const token = getCookie("sqa_token"); // Use Capital in constant values
  const [usersData, setUsersData] = useState([]);
  const [accessGroupData, setAccessGroupData] = useState([]);
  const [showInput, setShowInput] = useState(false);
  const [selectedUser, setSelectedUser] = useState("");
  const [selectedAccessGroup, setSelectedAccessGroup] = useState(-1);
  const [userEnabled, setUserEnabled] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [currentUserData, setCurentUserData] = useState([]);
  const [showError, setShowError] = useState(false); // Error state
  const [dropDownState, setDropDownState] = useState(-1); // Error state
  const [hasChanges, setHasChanges] = useState(false); // State to track changes

  useEffect(() => {
    getUsersDetail();
  }, []);

  useEffect(() => {
    if (dropDownState !== -1 || userEnabled !== true || userEnabled == true) {
      setHasChanges(true);
    } else {
      setHasChanges(false);
    }
  }, [dropDownState, userEnabled]);

  const getUsersDetail = async () => {
    try {
      const url = `/sqa/api/get_all_users/`;
      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (data?.result[0]) {
        setUsersData(data?.result[0]);
      } else {
        setUsersData([]);
      }
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const handleDropdownChange = (e) => {
    setSelectedAccessGroup(e.target.value);
    setDropDownState(e.target.value);
  };

  const manageUser = async () => {
    if (!selectedUser) {
      setShowError(true); // Show error if user is not selected
      return;
    }
    setShowError(false); // Hide error if user is selected

    try {
      const url = `/sqa/api/get_access_groups/`;
      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) throw new Error("Network response was not ok");

      const data = await response.json();
      if (data?.result[0]) {
        setAccessGroupData(data?.result);
      }

      setShowInput(true);

      // Call manageActiveUsers function asynchronously
      if (selectedUser) {
        const activeUsersData = await manageActiveUsers(selectedUser);

        if (activeUsersData) {
          setSelectedAccessGroup(activeUsersData?.result?.role_id);
          setCurentUserData([activeUsersData?.result]);
        }
      }
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const manageActiveUsers = async (email) => {
    try {
      const response = await fetch("/sqa/api/get_active_user/", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ email }), // Adjust payload as needed
      });

      if (!response.ok) throw new Error("Email API request failed");

      const data = await response.json();
      if (data?.result?.is_active == true) {
        setUserEnabled(true);
      } else {
        setUserEnabled(false);
      }
      return data;
    } catch (error) {
      console.error("Error sending email:", error);
      throw error; // Re-throw the error to handle it in the manageUser function
    }
  };

  const handleSaveChanges = async () => {
    if (!selectedUser) {
      toast.error("Please select a user.");
      return;
    }

    try {
      const url = `/sqa/api/update_user_access_group/`;
      const response = await fetch(url, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          email: selectedUser,
          role_id: parseInt(selectedAccessGroup),
          is_active: userEnabled,
        }),
      });

      if (!response.ok) throw new Error("Network response was not ok");

      const data = await response.json();

      if (data?.result.length !== 0) {
        const activeUsersData = await manageActiveUsers(selectedUser);

        if (activeUsersData) {
          setSelectedAccessGroup(activeUsersData?.result?.role_id);
          setCurentUserData([activeUsersData?.result]);
        }

        toast.success("User access updated successfully!");
      } else {
        toast.error(data.message || "Failed to update user access.");
      }

      setIsDialogOpen(false);
    } catch (error) {
      console.error("Error:", error);
      toast.error("Error updating user access.");
    }
  };

  const getRoleName = (roleId) => {
    const role = accessGroupData?.find((role) => role._id === roleId);
    return role ? role.role_name : "Unknown Role";
  };

  const handleOpenDialog = () => {
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
  };

  const handleChange = (e) => {
    const value = e.target.value;
    setSelectedUser(value);
    setShowInput(false);
  };

  const columns = [
    {
      name: "Name",
      selector: (row) => `${row.first_name} ${row.last_name}`,
    },
    {
      name: "Access Level",
      selector: (row) => getRoleName(row.role_id),
      cell: (row) => (
        <div style={{ wordBreak: "break-word", overflowWrap: "break-word" }}>
          {getRoleName(row.role_id)}
        </div>
      ),
    },
    {
      name: "Created By",
      selector: (row) => row.created_by,
      minWidth: "250px",
      cell: (row) => (
        <div style={{ wordBreak: "break-word", overflowWrap: "break-word" }}>
          {row.created_by}
        </div>
      ),
    },
    {
      name: "Last Edited By",
      selector: (row) => row.last_edited_by,
      minWidth: "250px",
      cell: (row) => (
        <div style={{ wordBreak: "break-word", overflowWrap: "break-word" }}>
          {row.last_edited_by}
        </div>
      ),
    },
    {
      name: "Created At",
      selector: (row) => row.created_at,
      minWidth: "170px",
    },
    {
      name: "Last Edited At",
      selector: (row) => row.last_edited_at,
      minWidth: "170px",
    },
  ];

  return (
    <div>
      <div
        className={`w-full flex flex-row gap-6 pt-3 ${
          showError ? "items-center" : "items-end"
        } `}
      >
        <div className="w-1/2">
          <label
            htmlFor="firstName"
            className="block text-sm font-medium text-gray-700 font-semibold text-[14px] pb-2"
          >
            Select User<span className="text-red-500">*</span>
          </label>
          <select
            id="usernames"
            value={selectedUser}
            onChange={handleChange}
            className="py-3 px-4 block w-full h-1/2  border border-gray-300 rounded-lg text-sm focus:border-blue-500 focus:ring-0"
          >
            <option value="">Select User</option>
            {usersData?.map((user, index) => (
              <option key={index} value={user.email}>
                {user.first_name} {user.last_name}
              </option>
            ))}
          </select>
          {showError && (
            <p className="text-red-500 text-sm">Please select a user</p>
          )}
        </div>
        <button
          onClick={manageUser}
          className="w-auto px-5 py-2 h-1/2 block bg-blue-500 block rounded-md text-white hover:bg-blue-600"
        >
          Submit
        </button>
      </div>
      {showInput && (
        <>
          <div className="w-full flex flex-row gap-6 pt-3 items-end w-full fade-in">
            <div className="w-1/2">
              <label
                htmlFor="firstName"
                className="block text-sm font-medium text-gray-700 font-semibold text-[14px] pb-2"
              >
                Access Groups<span className="text-red-500 ">*</span>
              </label>
              <select
                id="accessGroups"
                value={selectedAccessGroup}
                onChange={handleDropdownChange}
                className="py-3 px-4 block w-full border border-gray-300 rounded-lg text-sm focus:border-blue-500 focus:ring-0"
              >
                <option disabled value={-1}>
                  Select Access Group
                </option>
                {accessGroupData?.map((access, index) => (
                  <option key={index} value={access._id}>
                    {access.role_name}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex flex-col w-auto font-semibold text-[14px]">
              <label htmlFor="switch">Enable / Disable User</label>
              <Switch
                checked={userEnabled}
                onChange={(e) => setUserEnabled(e.target.checked)}
              />
            </div>
          </div>
          <div className="py-6">
            <h1 className="font-semibold text-[16px] py-2">
              User Access Details
            </h1>
            <DataTable
              columns={columns}
              data={currentUserData}
              customStyles={customStyles}
              highlightOnHover
              pointerOnHover
              striped
              responsive
            />
          </div>

          <div className="flex justify-center">
            <button
              disabled={hasChanges == false}
              onClick={handleOpenDialog}
              className="bg-[#4880FF] px-6 py-2 rounded-md text-white  hover:bg-blue-600"
            >
              Save Changes
            </button>
          </div>

          <AlertDialog
            open={isDialogOpen}
            onClose={handleCloseDialog}
            title="Manage User"
            description={`Confirm if you want to Update Access for ${currentUserData[0]?.first_name} ${currentUserData[0]?.last_name}`}
            onConfirm={handleSaveChanges}
            type="alert"
          />
        </>
      )}
    </div>
  );
};

export default AdminManageUser;
